import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Button,
  Modal,
  TextInput,
  SimpleTextArea,
  AlertService,
  ModalContent,
  ModalFooter,
  Checkbox,
  Theme,
  PencilIcon,
  TrashIcon,
  Select,
  SelectOption,
} from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import VTable from '../../components/v-table/v-table';
import VRowAction from '../../components/v-table/v-row-action';
import VColumn from '../../components/v-table/v-column';
import { GET_PROMPTS } from '../../graphql/queries';
import { CREATE_PROMPT, UPDATE_PROMPT, DELETE_PROMPT } from '../../graphql/mutations';

const useStyles = createUseStyles({
  root: {
    padding: 24,
  },
  addButton: {
    marginBottom: 16,
  },
  tableContainer: {
    width: '100%',
  },
  modalContent: {
    width: 652,
    display: 'flex',
    flexDirection: 'column',
  },
  fullWidth: {
    width: '100%',
  },
  label: {
    color: Theme.grey50,
    fontSize: 12,
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
});

const PromptManager = () => {
  const classes = useStyles();
  const { data, loading, refetch } = useQuery(GET_PROMPTS);
  const [createPrompt, { loading: createLoading }] = useMutation(CREATE_PROMPT);
  const [updatePrompt, { loading: updateLoading }] = useMutation(UPDATE_PROMPT);
  const [deletePrompt] = useMutation(DELETE_PROMPT);

  const [modalOpen, setModalOpen] = useState(false);
  const [prompt, setPrompt] = useState(null);

  const handleRowClick = (row) => {
    setPrompt({
      _id: row._id,
      name: row.name,
      description: row.description,
      template: row.template,
      active: row.active,
    });
    setModalOpen(true);
  };

  const handleAdd = () => {
    setPrompt({
      name: '',
      description: '',
      template: '',
      active: false,
    });
    setModalOpen(true);
  };

  const handleRemove = async (row) => {
    const prompts = data?.adminPromptsQuery?.prompts || [];
    const activeForName = prompts.filter((p) => p.name === row.name && p.active);
    if (row.active && activeForName.length === 1) {
      AlertService.alert({
        type: 'warning',
        message: <span>Cannot remove the only active prompt for this name.</span>,
        autoDismiss: true,
        dismissDelay: 4000,
      });
      return;
    }
    if (!window.confirm('Delete this prompt?')) return;
    try {
      await deletePrompt({ variables: { _id: row._id } });
      AlertService.alert({
        type: 'success',
        message: <span>Prompt deleted</span>,
        autoDismiss: true,
        dismissDelay: 3000,
      });
      refetch();
    } catch (err) {
      AlertService.alert({
        type: 'warning',
        message: <span>{err?.message || 'Error deleting prompt'}</span>,
        autoDismiss: true,
        dismissDelay: 4000,
      });
    }
  };

  const handleSave = async () => {
    if (!prompt.name.trim() || !prompt.template.trim()) {
      AlertService.alert({
        type: 'warning',
        message: <span>Name and template are required</span>,
        autoDismiss: true,
        dismissDelay: 3000,
      });
      return;
    }
    const prompts = data?.adminPromptsQuery?.prompts || [];
    const otherActive = prompts.find((p) => p.name === prompt.name && p.active && (!prompt || p._id !== prompt._id));
    if (
      prompt &&
      prompt.active &&
      !prompt.active &&
      prompts.filter((p) => p.name === prompt.name && p.active).length === 1
    ) {
      AlertService.alert({
        type: 'warning',
        message: <span>Cannot deactivate the only active prompt for this name.</span>,
        autoDismiss: true,
        dismissDelay: 4000,
      });
      return;
    }
    if (prompt.active && otherActive) {
      const confirmed = window.confirm(
        'Activating this prompt will deactivate the currently active prompt for this name. Do you want to continue?',
      );
      if (!confirmed) return;
    }
    try {
      const input = {
        ...prompt,
        name: prompt.name.toUpperCase(),
      };
      if (prompt._id) {
        await updatePrompt({ variables: { input } });
        AlertService.alert({
          type: 'success',
          message: <span>Prompt updated</span>,
          autoDismiss: true,
          dismissDelay: 3000,
        });
      } else {
        await createPrompt({ variables: { input } });
        AlertService.alert({
          type: 'success',
          message: <span>Prompt created</span>,
          autoDismiss: true,
          dismissDelay: 3000,
        });
      }
      setModalOpen(false);
      refetch();
    } catch (err) {
      if (err?.message?.includes('only active prompt')) {
        AlertService.alert({
          type: 'warning',
          message: <span>Cannot deactivate the only active prompt for this name.</span>,
          autoDismiss: true,
          dismissDelay: 4000,
        });
      } else {
        AlertService.alert({
          type: 'warning',
          message: <span>{err?.message || 'Error saving prompt'}</span>,
          autoDismiss: true,
          dismissDelay: 4000,
        });
      }
    }
  };

  const columns = [
    new VColumn({
      field: 'name',
      displayName: 'Name',
      defaultSort: true,
      defaultSortDirection: 'asc',
      resizable: true,
    }),
    new VColumn({ field: 'description', displayName: 'Description', resizable: true }),
    new VColumn({
      field: 'active',
      displayName: 'Active',
      formatter: (value) => (value ? 'Yes' : 'No'),
      resizable: true,
    }),
  ];

  const rowActions = [
    <VRowAction key="edit" icon={PencilIcon} onClick={(row) => () => handleRowClick(row)} tooltipText="Edit prompt" />,
    <VRowAction
      key="remove"
      icon={TrashIcon}
      warning
      onClick={(row) => () => handleRemove(row)}
      tooltipText="Delete prompt"
    />,
  ];

  const prompts = data?.adminPromptsQuery?.prompts || [];
  const promptNames = data?.adminPromptsQuery?.promptNames || [];

  return (
    <div className={classes.root}>
      <h1>Prompt Manager</h1>
      <Button onClick={handleAdd} className={classes.addButton}>
        Add Prompt
      </Button>
      <div className={classes.tableContainer}>
        <VTable
          data={prompts}
          cols={columns}
          loading={loading}
          rowActions={rowActions}
          onRowClick={handleRowClick}
          border
        />
      </div>
      <Modal open={modalOpen} onHide={() => setModalOpen(false)} title={prompt ? 'Edit Prompt' : 'Add Prompt'}>
        <ModalContent>
          <h2 className={classes.modalHeader}>{prompt ? 'Edit Prompt' : 'Add Prompt'}</h2>
          <div className={classes.modalContent}>
            <div style={{ marginBottom: 16 }}>
              <label className={classes.label} htmlFor="promptNameSelect">
                Prompt Name
              </label>
              <Select
                id="promptNameSelect"
                disabled={!!prompt?._id}
                required
                className={classes.fullWidth}
                selectedItem={prompt?.name ? { value: prompt.name, text: prompt.name } : null}
                onChange={(items) => {
                  const selected = items && items[0] ? items[0].value : '';
                  setPrompt({ ...prompt, name: selected });
                }}
                placeholderText="Select a prompt name"
              >
                {prompt?._id
                  ? [prompt?.name].map((n) => (
                      <SelectOption key={n} value={n} selected>
                        {n}
                      </SelectOption>
                    ))
                  : promptNames.map((n) => (
                      <SelectOption key={n} value={n} selected={prompt?.name === n}>
                        {n}
                      </SelectOption>
                    ))}
              </Select>
            </div>
            <TextInput
              value={prompt?.description}
              onChange={(value) => setPrompt({ ...prompt, description: value })}
              labelText="Description"
              className={classes.fullWidth}
              type="text"
            />
            <div>
              <label className={classes.label} htmlFor="promptTemplate">
                Prompt Template
              </label>
              <SimpleTextArea
                id="promptTemplate"
                value={prompt?.template}
                onChange={(value) => setPrompt({ ...prompt, template: value })}
                required
                className={classes.fullWidth}
                rows={6}
              />
            </div>
            <div className={classes.checkboxContainer}>
              <label className={classes.label} htmlFor="activeCheckbox">
                Active
              </label>
              <Checkbox
                id="activeCheckbox"
                checked={prompt?.active}
                onChecked={(checked) => setPrompt({ ...prompt, active: checked.checked })}
                label="Active"
              />
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" onClick={() => setModalOpen(false)} link>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createLoading || updateLoading}
            primary
            loading={createLoading || updateLoading}
            loadingText="Saving..."
            onClick={handleSave}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PromptManager;
